import { useState, useEffect, useCallback, useMemo, useRef } from "react";

const useColumnManager = ({
  data = [],
  screenCode = "DEFAULT",
  baseUrl = "",
  companyCode = "",
  defaultColumnPercents = {},
  generateColumnsFunction,
  request,
  defaultColumnsOrder = [],
}) => {
  const DEFAULT_PERCENT = 8;

  // State management
  const [tableWidth, setTableWidth] = useState(0);
  const [userResizedWidths, setUserResizedWidths] = useState({});
  const [columnWidths, setColumnWidths] = useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnsOrder, setColumnsOrder] = useState([]);
  const [showColumnManager, setShowColumnManager] = useState(false);

  // Drag and drop states
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Refs to prevent premature API overwrites
  const tableRef = useRef();
  const timeoutRef = useRef(null);
  const isConfigLoaded = useRef(false); // <-- CRITICAL: Tracks if API load finished

  const dataKeysString = useMemo(() => {
    if (!data || data.length === 0) return "";
    return Object.keys(data[0]).join(",");
  }, [data]);

  // Generate base columns from data
  const baseColumns = useMemo(() => {
    if (!data || data.length === 0) return [];
    return generateColumnsFunction ? generateColumnsFunction(data) : [];
  }, [dataKeysString, generateColumnsFunction]);

  // Extract field names from base columns
  const fieldNames = useMemo(
    () => baseColumns.map((col) => col.field),
    [baseColumns],
  );

  // Synchronize columnsOrder with fieldNames once data loads (if no API config exists yet)
  useEffect(() => {
    if (fieldNames.length > 0 && columnsOrder.length === 0) {
      const defaultOrder =
        defaultColumnsOrder.length > 0 ? defaultColumnsOrder : fieldNames;
      setColumnsOrder(defaultOrder);
    }
  }, [fieldNames, defaultColumnsOrder]);

  // Calculate column widths based on percentages
  const calculateColumnWidths = useCallback(
    (resizedPercents = userResizedWidths, baseWidth = tableWidth) => {
      const newWidths = {};
      fieldNames.forEach((field) => {
        const percent =
          resizedPercents[field] ??
          defaultColumnPercents[field] ??
          DEFAULT_PERCENT;
        newWidths[field] = Math.round((percent / 100) * baseWidth);
      });
      return newWidths;
    },
    [userResizedWidths, tableWidth, fieldNames, defaultColumnPercents],
  );

  // Initialize column visibility
  useEffect(() => {
    if (fieldNames.length > 0) {
      const initialVisibility = {};
      fieldNames.forEach((field) => {
        initialVisibility[field] = true;
      });
      setColumnVisibilityModel((prev) => {
        if (Object.keys(prev).length === 0) {
          return initialVisibility;
        }
        return prev;
      });
    }
  }, [fieldNames]);

  // Handle table width changes
  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.offsetWidth);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    handleResize();
    return () => resizeObserver.disconnect();
  }, []);
  const columnWidthsRef = useRef({});
  // Update column widths when table width or settings change
  useEffect(() => {
    if (tableWidth > 0) {
      const newWidths = calculateColumnWidths();
      const keys = Object.keys(newWidths);
      const changed = keys.some(
        (key) => newWidths[key] !== columnWidthsRef.current[key],
      );
      if (changed) {
        columnWidthsRef.current = newWidths;
        setColumnWidths({ ...newWidths }); // new state triggers re-render
      }
    }
  }, [tableWidth, calculateColumnWidths]);
  // Handle column resize
  const handleColumnResize = useCallback(
    (params) => {
      const field = params.colDef.field;
      const pixel = params.width;
      if (!tableRef.current) return;

      const base = tableRef.current.offsetWidth;
      const percent = (pixel / base) * 100;

      if (Math.abs((userResizedWidths[field] ?? 0) - percent) < 0.01) {
        return;
      }

      const updated = { ...userResizedWidths, [field]: percent };
      setUserResizedWidths(updated);

      const recalculated = calculateColumnWidths(updated, base);
      setColumnWidths(recalculated);
    },
    [userResizedWidths, calculateColumnWidths],
  );

  // Handle visibility change
  const handleVisibilityChange = useCallback((model) => {
    setColumnVisibilityModel(model);
  }, []);

  // Drag and drop handlers
  const handleDragStart = (e, field) => {
    setDraggedColumn(field);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverIndex(null);
    setIsDragging(false);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (!draggedColumn) return;

    const currentIndex = columnsOrder.indexOf(draggedColumn);
    if (currentIndex === -1 || currentIndex === targetIndex) return;

    const newOrder = [...columnsOrder];
    newOrder.splice(currentIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnsOrder(newOrder);
    setDraggedColumn(null);
    setDragOverIndex(null);
  };

  // Move column programmatically
  const moveColumn = (fromIndex, toIndex) => {
    const newOrder = [...columnsOrder];
    const [movedColumn] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedColumn);
    setColumnsOrder(newOrder);
  };

  // Reset column order
  const resetColumnOrder = () => {
    const resetOrder =
      defaultColumnsOrder.length > 0 ? defaultColumnsOrder : fieldNames;
    setColumnsOrder(resetOrder);
    setShowColumnManager(false);
  };

  // Toggle column visibility
  const toggleColumnVisibility = (field) => {
    const newVisibility = {
      ...columnVisibilityModel,
      [field]: !columnVisibilityModel[field],
    };

    let newWidths = { ...userResizedWidths };

    if (newVisibility[field]) {
      const prevWidth = userResizedWidths[field];
      newWidths[field] =
        prevWidth && prevWidth !== 0
          ? prevWidth
          : (defaultColumnPercents[field] ?? DEFAULT_PERCENT);
    } else {
      newWidths[field] = 0;
    }

    setColumnVisibilityModel(newVisibility);
    setUserResizedWidths(newWidths);
  };

  // Get column display name
  const getColumnDisplayName = (field) => {
    const column = baseColumns.find((col) => col.field === field);
    return column?.headerName || field;
  };

  // Build API strings for saving to backend
  const buildColumnStyleString = () => {
    return columnsOrder
      .map((field) => {
        const visible = columnVisibilityModel[field] !== false;
        const percent = visible
          ? (userResizedWidths[field] ??
            defaultColumnPercents[field] ??
            DEFAULT_PERCENT)
          : 0;
        return `${field}:${percent}`;
      })
      .join(";");
  };

  const buildColumnOrderString = () => {
    const orderToUse =
      columnsOrder.length > 0
        ? columnsOrder
        : defaultColumnsOrder.length > 0
          ? defaultColumnsOrder
          : fieldNames;
    return orderToUse.join(";");
  };

  // Send column config to API
  const sendColumnConfigToApi = async () => {
    // SECURITY GUARD: If config hasn't loaded yet, do not overwrite database!
    if (!isConfigLoaded.current) return;

    const CmpCode = companyCode;
    const C_style = buildColumnStyleString();
    const C_style2 = buildColumnOrderString();
    const C_style3 = "";
    const C_scrncode = screenCode;
    const c_user = "-";

    const payload = [
      {
        CmpCode,
        C_style,
        C_style2,
        C_style3,
        C_scrncode,
        c_user,
      },
    ];

    try {
      await fetch(`${baseUrl}/api/CRMScreenStyleReg`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Failed to send column config", err);
    }
  };

  // Load column config from API
  const loadColumnConfigFromApi = async () => {
    if (!baseUrl || !companyCode || !request) {
      isConfigLoaded.current = true; // No credentials, allow local adjustments
      return;
    }

    try {
      isConfigLoaded.current = false; // Reset lock on structural changes
      const url = `${baseUrl}/api/CpaysCount/${companyCode}/SCREENSTYLE/-/${screenCode}`;
      const response = await request({
        url: url,
        method: "GET",
        timeout: 5000,
      });

      if (Array.isArray(response) && response.length > 0) {
        const config = response[0];

        if (config?.C_style) {
          const stylePairs = config.C_style.split(";");
          const parsedWidths = {};
          const parsedVisibility = {};

          stylePairs.forEach((pair) => {
            const [field, widthStr] = pair.split(":");
            if (field && widthStr) {
              const width = parseFloat(widthStr);
              parsedWidths[field.trim()] = width;
              parsedVisibility[field.trim()] = width !== 0;
            }
          });

          setUserResizedWidths(parsedWidths);
          setColumnVisibilityModel((prev) => ({
            ...prev,
            ...parsedVisibility,
          }));
        }

        if (config?.C_style2) {
          const orderArray = config.C_style2.split(";").map((field) =>
            field.trim(),
          );

          const defaultOrder =
            defaultColumnsOrder.length > 0 ? defaultColumnsOrder : fieldNames;
          const newFields = defaultOrder.filter((f) => !orderArray.includes(f));
          setColumnsOrder([...orderArray, ...newFields]);
        }
      }
    } catch (err) {
      console.error("Failed to load column config from API", err);
    } finally {
      // Mark configuration loaded so future local state changes trigger auto-save safely
      isConfigLoaded.current = true;
    }
  };

  // Debounced API save
  useEffect(() => {
    if (!isConfigLoaded.current) return; // Prevent fire on mount/reset loops

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      sendColumnConfigToApi();
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [columnWidths, columnVisibilityModel, columnsOrder]);

  // Load config on mount/credential change
  useEffect(() => {
    loadColumnConfigFromApi();
  }, [baseUrl, companyCode, screenCode]);

  // Generate final columns with proper ordering and widths
  const finalColumns = useMemo(() => {
    const orderedFields = columnsOrder.length > 0 ? columnsOrder : fieldNames;
    return orderedFields
      .filter((field) => columnVisibilityModel[field] !== false)
      .map((field) => {
        const baseColumn = baseColumns.find((col) => col.field === field);
        if (!baseColumn) return null;
        return {
          ...baseColumn,
          width: columnWidthsRef.current[field] || baseColumn.minWidth || 100,
          flex: 0,
        };
      })
      .filter(Boolean);
  }, [columnsOrder, fieldNames, columnVisibilityModel, baseColumns]);

  return {
    tableRef,
    columnVisibilityModel,
    columnsOrder,
    showColumnManager,
    setShowColumnManager,
    draggedColumn,
    dragOverIndex,
    isDragging,
    finalColumns,
    handleColumnResize,
    handleVisibilityChange,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    moveColumn,
    resetColumnOrder,
    toggleColumnVisibility,
    getColumnDisplayName,
    buildColumnStyleString,
    buildColumnOrderString,
  };
};

export default useColumnManager;
